/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { ProductivityStop } from 'containers/new-route-manager/route-productivity/types'

/**
 * Staged bulk stop moves for the productivity compare view. Moves accumulate
 * as a pending overlay (rendered by `productivityMoves.ts`, published by
 * `useProductivityPublishMoves`) rather than committing on click, so a
 * dispatcher can stage a whole rebalance, read the recomputed P/L on both
 * route-days, and only then apply it.
 *
 * Ephemeral by design — a pending, unpublished move must not survive a reload,
 * so this stays out of the redux-persist whitelist.
 */
export const ROUTE_PRODUCTIVITY_MOVES_KEY = 'routeProductivityMoves' as const

export type MoveMode = 'current' | 'permanent'

/**
 * One staged move of one daily stop. `atSequence` is 1-based and only ever
 * feeds the overlay's splice position — position reaches the BE purely as list
 * order, never as a payload field. `stop` is carried so the destination row can
 * render the moved stop before any refetch.
 */
export interface PendingMove {
  workOrderId: string
  sourceRouteId: string
  sourceServiceDate: string
  targetRouteId: string
  targetServiceDate: string
  mode: MoveMode
  atSequence: number | null
  stop: ProductivityStop
}

type MoveMap = Record<string, PendingMove>

interface RouteProductivityMovesState {
  // Keyed by workOrderId, so re-moving a stop replaces its move rather than
  // staging a second removal of a stop that has already left its source day.
  present: MoveMap
  past: MoveMap[]
  future: MoveMap[]
  publishing: boolean
}

const initialState: RouteProductivityMovesState = {
  present: {},
  past: [],
  future: [],
  publishing: false,
}

// Every change to `present` snapshots the prior value so Cmd+Z can walk it
// back; a new branch of history discards any redo future.
const commit = (state: RouteProductivityMovesState, next: MoveMap): void => {
  state.past.push(state.present)
  state.future = []
  state.present = next
}

const routeProductivityMovesSlice = createSlice({
  name: ROUTE_PRODUCTIVITY_MOVES_KEY,
  initialState,
  reducers: {
    stageMoves: (state, action: PayloadAction<PendingMove[]>) => {
      if (action.payload.length === 0) return
      const next = { ...state.present }
      action.payload.forEach(move => {
        next[move.workOrderId] = move
      })
      commit(state, next)
    },
    undoMoves: state => {
      const previous = state.past.pop()
      if (previous === undefined) return
      state.future.unshift(state.present)
      state.present = previous
    },
    redoMoves: state => {
      const next = state.future.shift()
      if (next === undefined) return
      state.past.push(state.present)
      state.present = next
    },
    discardMoves: state => {
      if (Object.keys(state.present).length === 0) return
      commit(state, {})
    },
    movesPublishStarted: state => {
      state.publishing = true
    },
    // Drops one published group. Publishing clears history too: undoing back to
    // a state that has already been written to the BE would misrepresent what
    // is actually pending.
    movesPublished: (state, action: PayloadAction<string[]>) => {
      const published = new Set(action.payload)
      const next: MoveMap = {}
      Object.entries(state.present).forEach(([workOrderId, move]) => {
        if (!published.has(workOrderId)) next[workOrderId] = move
      })
      state.present = next
      state.past = []
      state.future = []
    },
    movesPublishFinished: state => {
      state.publishing = false
    },
    resetRouteProductivityMoves: () => initialState,
  },
})

export const {
  stageMoves,
  undoMoves,
  redoMoves,
  discardMoves,
  movesPublishStarted,
  movesPublished,
  movesPublishFinished,
  resetRouteProductivityMoves,
} = routeProductivityMovesSlice.actions

export interface RouteProductivityMovesRootStateSlice {
  [ROUTE_PRODUCTIVITY_MOVES_KEY]?: RouteProductivityMovesState
}

const selectState = (state: RouteProductivityMovesRootStateSlice): RouteProductivityMovesState =>
  state[ROUTE_PRODUCTIVITY_MOVES_KEY] ?? initialState

export const selectPendingMoveMap = (state: RouteProductivityMovesRootStateSlice): MoveMap => selectState(state).present

export const selectPendingMoveCount = (state: RouteProductivityMovesRootStateSlice): number =>
  Object.keys(selectState(state).present).length

export const selectCanUndoMoves = (state: RouteProductivityMovesRootStateSlice): boolean => selectState(state).past.length > 0

export const selectCanRedoMoves = (state: RouteProductivityMovesRootStateSlice): boolean => selectState(state).future.length > 0

export const selectIsPublishingMoves = (state: RouteProductivityMovesRootStateSlice): boolean => selectState(state).publishing

export { initialState as routeProductivityMovesInitialState }

export default routeProductivityMovesSlice.reducer
